import { useState, useEffect, useRef } from "react";

// ── LUXURY CURSOR (desktop only) ───────────────────────────────────────────────
function LuxuryCursor() {
  const dotRef  = useRef(null);
  const ringRef = useRef(null);
  const haloRef = useRef(null);
  const pos     = useRef({ x: -200, y: -200 });
  const ringPos = useRef({ x: -200, y: -200 });
  const haloPos = useRef({ x: -200, y: -200 });
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const onMove = (e) => {
      pos.current = { x: e.clientX, y: e.clientY };
      if (dotRef.current) {
        dotRef.current.style.left = `${e.clientX - 4}px`;
        dotRef.current.style.top  = `${e.clientY - 4}px`;
      }
    };
    let raf;
    const tick = () => {
      ringPos.current.x += (pos.current.x - ringPos.current.x) * 0.13;
      ringPos.current.y += (pos.current.y - ringPos.current.y) * 0.13;
      haloPos.current.x += (pos.current.x - haloPos.current.x) * 0.06;
      haloPos.current.y += (pos.current.y - haloPos.current.y) * 0.06;
      const rSz = hovered ? 50 : 34;
      if (ringRef.current) {
        ringRef.current.style.left   = `${ringPos.current.x - rSz / 2}px`;
        ringRef.current.style.top    = `${ringPos.current.y - rSz / 2}px`;
        ringRef.current.style.width  = `${rSz}px`;
        ringRef.current.style.height = `${rSz}px`;
      }
      if (haloRef.current) {
        haloRef.current.style.left = `${haloPos.current.x - 36}px`;
        haloRef.current.style.top  = `${haloPos.current.y - 36}px`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    window.addEventListener("mousemove", onMove);
    const attach = () => document.querySelectorAll("a,button").forEach(el => {
      el.addEventListener("mouseenter", () => setHovered(true));
      el.addEventListener("mouseleave", () => setHovered(false));
    });
    setTimeout(attach, 600);
    return () => { window.removeEventListener("mousemove", onMove); cancelAnimationFrame(raf); };
  }, [hovered]);

  return <>
    <div ref={dotRef} style={{ position:"fixed",top:0,left:0,width:8,height:8,borderRadius:"50%",pointerEvents:"none",zIndex:99999,background:"radial-gradient(circle at 35% 35%,#fff 0%,#c9a84c 60%,#b8860b 100%)",boxShadow:"0 0 6px 2px rgba(201,168,76,0.7)" }} />
    <div ref={ringRef} style={{ position:"fixed",top:0,left:0,borderRadius:"50%",pointerEvents:"none",zIndex:99998,border:`1px solid rgba(201,168,76,${hovered?0.9:0.55})`,boxShadow:hovered?"0 0 10px 2px rgba(201,168,76,0.3)":"none",transition:"width 0.3s,height 0.3s,border 0.3s" }} />
    <div ref={haloRef} style={{ position:"fixed",top:0,left:0,width:72,height:72,borderRadius:"50%",pointerEvents:"none",zIndex:99997,background:"radial-gradient(circle,rgba(201,168,76,0.08) 0%,transparent 70%)",opacity:hovered?1:0.5 }} />
  </>;
}

// ── 3D TILT CARD ───────────────────────────────────────────────────────────────
function TiltCard({ children, style: ps = {}, intensity = 10, disabled = false }) {
  const ref = useRef(null);
  const [ts, setTs] = useState({});
  const [glow, setGlow] = useState({ x: 50, y: 50 });
  const onMove = (e) => {
    if (disabled) return;
    const r = ref.current?.getBoundingClientRect(); if (!r) return;
    const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
    setTs({ transform:`perspective(900px) rotateX(${(y-.5)*-intensity}deg) rotateY(${(x-.5)*intensity}deg) scale3d(1.025,1.025,1.025)`,transition:"transform 0.08s ease-out" });
    setGlow({ x:x*100,y:y*100 });
  };
  const onLeave = () => { if (!disabled) setTs({ transform:"perspective(900px) rotateX(0) rotateY(0) scale3d(1,1,1)",transition:"transform 0.5s ease-out" }); };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{ ...ts, transformStyle:"preserve-3d", ...ps }}>
      <div style={{ position:"relative",overflow:"hidden",borderRadius:"inherit" }}>
        <div style={{ position:"absolute",inset:0,zIndex:1,pointerEvents:"none",borderRadius:"inherit",background:`radial-gradient(circle at ${glow.x}% ${glow.y}%,rgba(201,168,76,0.1) 0%,transparent 65%)`,transition:"background 0.12s" }} />
        <div style={{ position:"relative",zIndex:2 }}>{children}</div>
      </div>
    </div>
  );
}

// ── SECTION TITLE ──────────────────────────────────────────────────────────────
function SectionTitle({ children, sub }) {
  return (
    <div style={{ textAlign:"center",marginBottom:"3rem" }}>
      <h2 style={{ fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:"clamp(1.8rem,4vw,3rem)",letterSpacing:"-0.01em",margin:"0 0 0.5rem",color:"#1a1008" }}>{children}</h2>
      <div style={{ width:48,height:1.5,margin:"0 auto 0.6rem",background:"linear-gradient(90deg,transparent,#c9a84c,transparent)" }} />
      {sub && <p style={{ fontSize:14,color:"rgba(0,0,0,0.4)",margin:0,fontFamily:"'DM Sans',sans-serif",padding:"0 1rem" }}>{sub}</p>}
    </div>
  );
}

// ── GOLD DUST ─────────────────────────────────────────────────────────────────
function GoldDust() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    const resize = () => { c.width=window.innerWidth; c.height=window.innerHeight; };
    resize();
    window.addEventListener("resize",resize);
    const pts = Array.from({length:35},()=>({ x:Math.random()*window.innerWidth,y:Math.random()*window.innerHeight,r:Math.random()*1.6+0.3,dx:(Math.random()-.5)*0.18,dy:-(Math.random()*0.25+0.05),op:Math.random()*0.35+0.1 }));
    let raf;
    const draw = () => {
      ctx.clearRect(0,0,c.width,c.height);
      pts.forEach(p=>{ ctx.beginPath();ctx.arc(p.x,p.y,p.r,0,Math.PI*2);ctx.fillStyle=`rgba(201,168,76,${p.op})`;ctx.fill();p.x+=p.dx;p.y+=p.dy;if(p.y<-10){p.y=c.height+10;p.x=Math.random()*c.width;}if(p.x<-5||p.x>c.width+5)p.x=Math.random()*c.width; });
      raf=requestAnimationFrame(draw);
    };
    draw();
    return ()=>{ cancelAnimationFrame(raf);window.removeEventListener("resize",resize); };
  },[]);
  return <canvas ref={canvasRef} style={{ position:"fixed",inset:0,pointerEvents:"none",zIndex:0 }} />;
}

const frost = { background:"rgba(255,255,255,0.62)",border:"1px solid rgba(201,168,76,0.18)",backdropFilter:"blur(28px) saturate(200%)",WebkitBackdropFilter:"blur(28px) saturate(200%)",borderRadius:20,boxShadow:"0 8px 40px rgba(0,0,0,0.06),0 1px 0 rgba(255,255,255,0.9) inset" };
const chip = { padding:"4px 10px",borderRadius:999,fontSize:11,border:"1px solid rgba(201,168,76,0.25)",background:"rgba(201,168,76,0.08)",color:"#7a5c1e",fontFamily:"'DM Sans',sans-serif" };

// ── MAIN ───────────────────────────────────────────────────────────────────────
export default function Portfolio() {
  const [active, setActive]     = useState("about");
  const [scrollY, setScrollY]   = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);

    if (!document.getElementById("luxury-font")) {
      const l = document.createElement("link"); l.id="luxury-font"; l.rel="stylesheet";
      l.href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,800;1,600&family=DM+Sans:wght@300;400;500;600&display=swap";
      document.head.appendChild(l);
    }
    if (!document.getElementById("luxury-styles")) {
      const s = document.createElement("style"); s.id="luxury-styles";
      s.textContent = `
        *{box-sizing:border-box;}
        @media(hover:hover){*{cursor:none!important;}}
        body{margin:0;background:#f5f0e8;overflow-x:hidden;}
        @keyframes goldShimmer{0%,100%{background-position:0% 50%}50%{background-position:100% 50%}}
        @keyframes floatUp{0%{transform:translateY(0)}100%{transform:translateY(-30px)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes shimLine{0%{left:-60%}100%{left:130%}}
        @keyframes slideDown{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}
        .chip:hover{background:rgba(201,168,76,0.2)!important;border-color:rgba(201,168,76,0.5)!important;color:#5a3e0a!important;transform:translateY(-2px);}
        .chip{transition:all 0.18s ease;}
        .nav-btn:hover{color:#b8860b!important;}
        .pill:hover{background:rgba(201,168,76,0.1)!important;border-color:rgba(201,168,76,0.35)!important;}
        .pbtn:hover{filter:brightness(1.1);transform:translateY(-1px);}
        .sbtn:hover{background:rgba(201,168,76,0.12)!important;}
        .pbtn,.sbtn{transition:all 0.2s;}
        .cert:hover{background:rgba(201,168,76,0.1)!important;border-color:rgba(201,168,76,0.3)!important;}
        .cert{transition:all 0.2s;}
        html{scroll-behavior:smooth;}
        ::-webkit-scrollbar{width:4px;}
        ::-webkit-scrollbar-track{background:#f0ebe0;}
        ::-webkit-scrollbar-thumb{background:rgba(201,168,76,0.4);border-radius:3px;}
      `;
      document.head.appendChild(s);
    }
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive:true });
    return () => { window.removeEventListener("scroll",onScroll); window.removeEventListener("resize",checkMobile); };
  }, []);

  useEffect(() => { if (menuOpen) setMenuOpen(false); }, [scrollY]);

  const navA = Math.min(scrollY/80, 0.97);
  const scrollTo = (id) => { setActive(id); setMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior:"smooth" }); };
  const open = (url) => url && window.open(url,"_blank","noopener,noreferrer");

  const NAV_LINKS = ["about","projects","skills","education"];

  const RESUME_VIEW = "https://drive.google.com/file/d/1q1RDNWW71JqVoM1KhGJu6wOi6bA2BUkY/view?usp=sharing";
  const RESUME_DL   = "https://drive.google.com/uc?export=download&id=1q1RDNWW71JqVoM1KhGJu6wOi6bA2BUkY";

  const projects = [
    {
      title: "AI LifeCard",
      sub: "AI-Powered Life Intelligence Dashboard",
      desc: "Full-stack AI platform that scores financial health, career trajectory & burnout risk for 500+ users using a dual ML engine — RandomForest (R²=0.87) for pattern classification and IsolationForest for anomaly detection. Features a context-aware Gemini chatbot that references real user scores to answer personal finance questions. Deployed on Railway (Flask API) + Vercel (React) with Firebase authentication.",
      tech: ["React", "Python", "Flask", "Firebase Auth", "scikit-learn", "RandomForest", "IsolationForest", "SQLite", "Railway", "Vercel"],
      live: "https://ai-lifecard.vercel.app",
      gh:   "https://github.com/Janhavi-cmd/ai-lifecard",
      icon: "🧠",
      accent: "#b8860b",
    },
    {
      title: "NewsWave",
      sub: "Multilingual Real-Time News Hub",
      desc: "React + Vite news aggregator delivering real-time headlines across 12 languages via The Guardian API, with AI-powered article summarization, Web Speech API voice search, and embedded YouTube video integration. Features category filtering, dark/light mode, and responsive design — deployed on Vercel with zero-backend architecture using client-side API orchestration.",
      tech: ["React", "Vite", "Tailwind CSS", "Axios", "Guardian API", "YouTube Data API v3", "Web Speech API", "AI Summarization", "Vercel"],
      live: "https://newswave-app-six.vercel.app/",
      gh:   "https://github.com/Janhavi-cmd/newswave-app",
      icon: "🌊",
      accent: "#6b7c93",
    },
    {
      title: "SpendSmart",
      sub: "AI-Powered Expense Tracker",
      desc: "Production-grade expense management platform for 500+ users featuring Google Gemini AI for spending insights, budget recommendations & a natural-language finance chat assistant. Built with Flask + SQLAlchemy, role-based admin panel with platform analytics, PDF export, 9 smart categories including lent-money tracking, and dark/light mode — live on Render.",
      tech: ["Flask", "Python", "SQLAlchemy", "SQLite", "Gemini AI", "Chart.js", "JWT Auth", "FPDF", "Render"],
      live: "https://expense-tracker-fowr.onrender.com/login",
      gh:   "https://github.com/Janhavi-cmd/SpendSmart",
      icon: "💰",
      accent: "#7a6a3e",
    },
    // ── VAULTIQ — NEW PROJECT ──────────────────────────────────────────────────
    {
      title: "VaultIQ",
      sub: "AI-Powered Smart Investor Dashboard",
      desc: "Institutional-grade fintech intelligence platform covering 80+ global & NSE stocks — featuring a proprietary Decision Score engine (BUY/HOLD/RISKY), Vaultara AI analyst with ELI5 mode, 7-day ML price forecast, Z-score anomaly detection, and a What-If Investment Simulator. Includes 3 Jupyter notebooks: full EDA, ML model comparison (Logistic Regression vs Random Forest vs XGBoost), and 15 SQL analytics queries with window functions & CTEs.",
      tech: ["Python", "FastAPI", "pandas", "scikit-learn", "XGBoost", "SQLite", "Chart.js", "WebSocket", "yfinance", "Docker", "GitHub Pages", "JavaScript"],
      live: "https://janhavi-cmd.github.io/VaultIQ/",
      gh:   "https://github.com/Janhavi-cmd/VaultIQ",
      icon: "📈",
      accent: "#c9a84c",
    },
  ];

  const skills = {
    "Languages":          ["Python","JavaScript","SQL"],
    "Frontend":           ["React.js","Vite","Tailwind CSS","Chart.js","HTML5","CSS3"],
    "Backend":            ["Flask","FastAPI","RESTful APIs","SQLAlchemy ORM","JWT Authentication"],
    "ML & AI":            ["scikit-learn","pandas","NumPy","Matplotlib","Seaborn","RandomForest","XGBoost","IsolationForest","Feature Engineering","EDA"],
    "Databases":          ["SQLite","SQL Analytics","CTEs","Window Functions"],
    "APIs & Integration": ["Firebase Auth","Guardian API","YouTube Data API v3","Web Speech API","Google Gemini AI","yfinance","Axios"],
    "Tools & Cloud":      ["Git","GitHub","Vercel","Railway","Render","GitHub Pages","Docker","Jupyter Notebook","VS Code","Postman"],
  };

  const certs = [
    { name:"JP Morgan · Software Engineering",                    org:"Forage 2026 " },
    { name:"Citi · Software Development",                         org:"Forage 2026 " },
    { name:"TechA AWS Certified Developer",                       org:"Infosys Springboard" },
    { name:"Full-Stack Web Development Bootcamp",                 org:"Udemy" },
    { name:"Data Science, Machine Learning,DL, NLP Bootcamp",     org:"Udemy" },

  ];

  return (
    <div style={{ minHeight:"100vh",background:"#f5f0e8",color:"#1a1008",fontFamily:"'DM Sans',sans-serif",overflowX:"hidden" }}>
      {!isMobile && <LuxuryCursor />}
      <GoldDust />

      {/* ── BACKGROUND ── */}
      <div style={{ position:"fixed",inset:0,zIndex:0,pointerEvents:"none" }}>
        <div style={{ position:"absolute",inset:0,background:"linear-gradient(135deg,#fdf8f0 0%,#f5f0e8 40%,#ede8dc 100%)" }} />
        <div style={{ position:"absolute",top:"-10%",right:"-8%",width:"min(600px,80vw)",height:"min(600px,80vw)",borderRadius:"50%",background:"radial-gradient(circle,rgba(201,168,76,0.12) 0%,transparent 70%)",filter:"blur(60px)",animation:"floatUp 12s ease-in-out infinite alternate" }} />
        <div style={{ position:"absolute",bottom:"-8%",left:"-5%",width:"min(500px,70vw)",height:"min(500px,70vw)",borderRadius:"50%",background:"radial-gradient(circle,rgba(220,180,120,0.1) 0%,transparent 70%)",filter:"blur(50px)",animation:"floatUp 15s ease-in-out 2s infinite alternate" }} />
        <div style={{ position:"absolute",inset:0,backgroundImage:"radial-gradient(rgba(184,134,11,0.06) 1px,transparent 1px)",backgroundSize:"32px 32px" }} />
      </div>

      {/* ══════════════════════════════════
          NAVBAR
      ══════════════════════════════════ */}
      <nav style={{ position:"fixed",top:0,width:"100%",zIndex:1000,background:`rgba(245,240,232,${navA*0.95})`,backdropFilter:navA>0.05?"blur(24px) saturate(180%)":"none",WebkitBackdropFilter:navA>0.05?"blur(24px) saturate(180%)":"none",borderBottom:navA>0.05?"1px solid rgba(201,168,76,0.18)":"none",transition:"all 0.3s" }}>
        <div style={{ maxWidth:1180,margin:"0 auto",padding:"0.85rem 1.2rem",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
          <div style={{ display:"flex",alignItems:"center",gap:10,flexShrink:0 }}>
            <div style={{ width:36,height:36,borderRadius:10,background:"linear-gradient(135deg,#c9a84c,#8b6914)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:14,color:"#fff",boxShadow:"0 4px 14px rgba(184,134,11,0.35)",flexShrink:0 }}>JC</div>
            <span style={{ fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:isMobile?14:16,background:"linear-gradient(90deg,#8b6914,#c9a84c,#8b6914)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",whiteSpace:"nowrap" }}>
              Janhavi Chaturvedi
            </span>
          </div>
          {!isMobile && (
            <div style={{ display:"flex",gap:"1.6rem",alignItems:"center" }}>
              {NAV_LINKS.map(s => (
                <button key={s} className="nav-btn" onClick={()=>scrollTo(s)} style={{ background:"none",border:"none",color:active===s?"#b8860b":"rgba(0,0,0,0.45)",fontFamily:"'DM Sans',sans-serif",fontSize:13.5,fontWeight:500,textTransform:"capitalize",borderBottom:active===s?"1px solid #c9a84c":"1px solid transparent",paddingBottom:2,transition:"all 0.2s" }}>{s}</button>
              ))}
              <a href={RESUME_DL} target="_blank" rel="noopener noreferrer" style={{ padding:"7px 18px",borderRadius:9,fontSize:13,fontWeight:600,background:"linear-gradient(135deg,#c9a84c,#8b6914)",color:"#fff",textDecoration:"none",display:"flex",alignItems:"center",gap:5,boxShadow:"0 3px 14px rgba(184,134,11,0.3)",fontFamily:"'DM Sans',sans-serif" }}>↓ Resume</a>
            </div>
          )}
          {isMobile && (
            <button onClick={()=>setMenuOpen(!menuOpen)} style={{ background:"none",border:"none",padding:"6px",display:"flex",flexDirection:"column",gap:5,justifyContent:"center",alignItems:"center",width:36,height:36 }}>
              <span style={{ display:"block",width:22,height:2,background:"#8b6914",borderRadius:2,transition:"all 0.3s",transform:menuOpen?"rotate(45deg) translate(5px,5px)":"none" }} />
              <span style={{ display:"block",width:22,height:2,background:"#8b6914",borderRadius:2,transition:"all 0.3s",opacity:menuOpen?0:1 }} />
              <span style={{ display:"block",width:22,height:2,background:"#8b6914",borderRadius:2,transition:"all 0.3s",transform:menuOpen?"rotate(-45deg) translate(5px,-5px)":"none" }} />
            </button>
          )}
        </div>
        {isMobile && menuOpen && (
          <div style={{ position:"absolute",top:"100%",left:0,right:0,background:"rgba(245,240,232,0.98)",backdropFilter:"blur(24px)",borderBottom:"1px solid rgba(201,168,76,0.2)",animation:"slideDown 0.25s ease both",zIndex:999 }}>
            <div style={{ padding:"0.5rem 1.2rem 1rem",display:"flex",flexDirection:"column",gap:"0.2rem" }}>
              {NAV_LINKS.map(s => (
                <button key={s} onClick={()=>scrollTo(s)} style={{ background:"none",border:"none",textAlign:"left",padding:"0.75rem 0.5rem",fontFamily:"'DM Sans',sans-serif",fontSize:16,fontWeight:500,color:active===s?"#b8860b":"rgba(0,0,0,0.6)",textTransform:"capitalize",borderBottom:"1px solid rgba(201,168,76,0.1)",borderLeft:active===s?"3px solid #c9a84c":"3px solid transparent",paddingLeft:active===s?"0.8rem":"0.5rem",transition:"all 0.2s" }}>{s}</button>
              ))}
              <a href={RESUME_DL} target="_blank" rel="noopener noreferrer" style={{ marginTop:"0.6rem",padding:"12px",borderRadius:10,fontSize:14,fontWeight:600,background:"linear-gradient(135deg,#c9a84c,#8b6914)",color:"#fff",textDecoration:"none",textAlign:"center",boxShadow:"0 3px 14px rgba(184,134,11,0.3)",fontFamily:"'DM Sans',sans-serif" }}>↓ Download Resume</a>
            </div>
          </div>
        )}
      </nav>

      {/* ══════════════════════════════════
          HERO
      ══════════════════════════════════ */}
      <section id="about" style={{ position:"relative",zIndex:1,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:isMobile?"6rem 1.2rem 3rem":"7rem 2rem 5rem" }}>
        <div style={{ maxWidth:860,width:"100%",textAlign:"center",animation:"fadeIn 0.9s ease both" }}>
          <div style={{ width:50,height:1,background:"linear-gradient(90deg,transparent,#c9a84c,transparent)",margin:"0 auto 1.4rem",animation:"fadeUp 0.6s ease both" }} />
          <div style={{ display:"inline-flex",alignItems:"center",gap:6,padding:"6px 16px",borderRadius:999,marginBottom:"1.6rem",border:"1px solid rgba(201,168,76,0.3)",background:"rgba(255,255,255,0.7)",backdropFilter:"blur(12px)",fontSize:isMobile?11.5:12.5,color:"#7a5c1e",fontFamily:"'DM Sans',sans-serif",letterSpacing:"0.02em",boxShadow:"0 2px 12px rgba(201,168,76,0.12)",animation:"fadeUp 0.7s ease 0.1s both",textAlign:"center" }}>
            ✦ Open to Full-Stack, AI/ML &amp; Fintech Engineering Roles ✦
          </div>
          <h1 style={{ fontFamily:"'Playfair Display',serif",fontWeight:800,lineHeight:0.95,fontSize:isMobile?"clamp(3rem,14vw,4.5rem)":"clamp(3.5rem,10vw,8rem)",letterSpacing:"-0.02em",marginBottom:"1.1rem",background:"linear-gradient(135deg,#1a1008 0%,#5a3e0a 25%,#c9a84c 50%,#8b6914 75%,#3d2800 100%)",backgroundSize:"300% 300%",animation:"goldShimmer 5s ease infinite,fadeUp 0.8s ease 0.2s both",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" }}>
            Janhavi<br />Chaturvedi
          </h1>
          <p style={{ fontFamily:"'Playfair Display',serif",fontWeight:600,fontStyle:"italic",fontSize:isMobile?"clamp(0.85rem,3.5vw,1rem)":"clamp(0.95rem,2.2vw,1.2rem)",color:"#7a5c1e",marginBottom:"1rem",letterSpacing:"0.03em",animation:"fadeUp 0.8s ease 0.3s both",padding:"0 0.5rem" }}>
            AI/ML Engineer &nbsp;·&nbsp; Full-Stack Developer &nbsp;·&nbsp; Fintech & Data Specialist
          </p>
          <div style={{ width:60,height:1,background:"linear-gradient(90deg,transparent,#c9a84c,transparent)",margin:"0 auto 1rem",animation:"fadeUp 0.8s ease 0.35s both" }} />
          {/* ── UPDATED: 4 projects, added Fintech mention ── */}
          <p style={{ fontSize:isMobile?14:15.5,color:"rgba(0,0,0,0.48)",maxWidth:640,margin:"0 auto 0.8rem",lineHeight:1.8,animation:"fadeUp 0.8s ease 0.4s both",fontFamily:"'DM Sans',sans-serif",padding:"0 0.5rem" }}>
            MCA 2026 · Delivered <span style={{ color:"#8b6914",fontWeight:600 }}>4 live AI-powered projects</span> — ML dashboards, real-time apps, fintech intelligence platforms — serving <span style={{ color:"#c0674e",fontWeight:600 }}>500+ users</span>, all deployed and production-ready.
          </p>
          <p style={{ fontSize:isMobile?12:12.5,color:"#2d7a4e",fontWeight:600,marginBottom:"2rem",letterSpacing:"0.03em",animation:"fadeUp 0.8s ease 0.45s both",padding:"0 0.5rem" }}>
            🟢&nbsp; Actively seeking Full-Time Roles &amp; Internships · Mumbai-based, open to Remote
          </p>
          <div style={{ display:"flex",justifyContent:"center",gap:8,flexWrap:"wrap",marginBottom:"1.8rem",animation:"fadeUp 0.8s ease 0.5s both",padding:"0 0.5rem" }}>
            {[
              { icon:"✉", label:isMobile?"Email":"janhavichaturvedi0511@gmail.com", href:"mailto:janhavichaturvedi0511@gmail.com" },
              { icon:"in", label:"LinkedIn",     href:"https://www.linkedin.com/in/janhavi-chaturvedi-a5590435a/" },
              { icon:"⌥",  label:"GitHub",       href:"https://github.com/Janhavi-cmd" },
              { icon:"◎",  label:"Mumbai, India", href:null },
            ].map((c,i) => (
              c.href
                ? <a key={i} href={c.href} target={c.href.startsWith("mailto")?undefined:"_blank"} rel="noopener noreferrer" className="pill" style={{ display:"flex",alignItems:"center",gap:5,padding:"8px 13px",borderRadius:10,border:"1px solid rgba(201,168,76,0.2)",background:"rgba(255,255,255,0.65)",color:"rgba(0,0,0,0.6)",fontSize:isMobile?12:13,textDecoration:"none",backdropFilter:"blur(12px)",transition:"all 0.2s",boxShadow:"0 2px 8px rgba(0,0,0,0.05)" }}>
                    <span style={{ color:"#c9a84c",fontSize:11 }}>{c.icon}</span>{c.label}
                  </a>
                : <div key={i} style={{ display:"flex",alignItems:"center",gap:5,padding:"8px 13px",borderRadius:10,border:"1px solid rgba(201,168,76,0.2)",background:"rgba(255,255,255,0.65)",color:"rgba(0,0,0,0.6)",fontSize:isMobile?12:13,backdropFilter:"blur(12px)",boxShadow:"0 2px 8px rgba(0,0,0,0.05)" }}>
                    <span style={{ color:"#c9a84c",fontSize:11 }}>{c.icon}</span>{c.label}
                  </div>
            ))}
          </div>
          <div style={{ display:"flex",justifyContent:"center",gap:12,flexWrap:"wrap",animation:"fadeUp 0.8s ease 0.55s both",padding:"0 0.5rem" }}>
            <a href={RESUME_VIEW} target="_blank" rel="noopener noreferrer" className="pbtn" style={{ display:"flex",alignItems:"center",gap:7,padding:isMobile?"11px 24px":"13px 30px",borderRadius:12,fontWeight:600,fontSize:isMobile?13:14,background:"linear-gradient(135deg,#c9a84c,#8b6914)",color:"#fff",textDecoration:"none",boxShadow:"0 6px 22px rgba(184,134,11,0.35)",fontFamily:"'DM Sans',sans-serif" }}>◎ View Resume</a>
            <a href={RESUME_DL} className="sbtn" style={{ display:"flex",alignItems:"center",gap:7,padding:isMobile?"11px 24px":"13px 30px",borderRadius:12,fontWeight:600,fontSize:isMobile?13:14,border:"1.5px solid rgba(201,168,76,0.4)",background:"rgba(255,255,255,0.7)",color:"#8b6914",textDecoration:"none",fontFamily:"'DM Sans',sans-serif",backdropFilter:"blur(12px)",boxShadow:"0 4px 16px rgba(0,0,0,0.06)" }}>↓ Download CV</a>
          </div>
          <div style={{ width:50,height:1,background:"linear-gradient(90deg,transparent,#c9a84c,transparent)",margin:"2.5rem auto 0",animation:"fadeUp 0.8s ease 0.6s both" }} />
        </div>
      </section>

      {/* ══════════════════════════════════
          PROJECTS
      ══════════════════════════════════ */}
      <section id="projects" style={{ position:"relative",zIndex:1,padding:isMobile?"3rem 1.2rem":"5rem 2rem" }}>
        <div style={{ maxWidth:1180,margin:"0 auto" }}>
          {/* ── UPDATED subtitle: 4 projects ── */}
          <SectionTitle sub="4 live AI-powered projects — click to explore">Featured Projects</SectionTitle>
          <div style={{ display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fit,minmax(290px,1fr))",gap:"1.2rem" }}>
            {projects.map((p,i) => (
              <TiltCard key={i} intensity={10} disabled={isMobile} style={{ borderRadius:20 }}>
                <div style={{ ...frost,padding:isMobile?"1.4rem":"1.8rem",display:"flex",flexDirection:"column",
                  /* Gold border glow for VaultIQ */
                  ...(p.isNew ? { border:"1.5px solid rgba(201,168,76,0.45)", boxShadow:"0 8px 40px rgba(201,168,76,0.12),0 1px 0 rgba(255,255,255,0.9) inset" } : {})
                }}>
                  <div style={{ height:2,borderRadius:2,background:`linear-gradient(90deg,${p.accent},transparent)`,marginBottom:"1.2rem",position:"relative",overflow:"hidden" }}>
                    <div style={{ position:"absolute",top:0,left:"-60%",width:"60%",height:"100%",background:"linear-gradient(90deg,transparent,rgba(255,255,255,0.8),transparent)",animation:"shimLine 2.5s ease infinite" }} />
                  </div>
                  <div style={{ display:"flex",gap:11,alignItems:"flex-start",marginBottom:"0.8rem" }}>
                    <div style={{ width:42,height:42,borderRadius:12,background:`${p.accent}12`,border:`1px solid ${p.accent}28`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:19,flexShrink:0 }}>{p.icon}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" }}>
                        <h3 style={{ fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:isMobile?15:16.5,color:"#1a1008",margin:0,lineHeight:1.2 }}>{p.title}</h3>
                        {/* NEW badge for VaultIQ */}
                        {p.isNew && (
                          <span style={{ fontSize:9,padding:"2px 7px",borderRadius:99,background:"linear-gradient(135deg,#c9a84c,#8b6914)",color:"#fff",fontWeight:700,fontFamily:"'DM Sans',sans-serif",letterSpacing:"0.05em" }}>NEW</span>
                        )}
                      </div>
                      <p style={{ fontSize:11,color:p.accent,margin:"3px 0 0",fontWeight:600,fontFamily:"'DM Sans',sans-serif" }}>{p.sub}</p>
                    </div>
                  </div>
                  <p style={{ fontSize:isMobile?13:13.5,color:"rgba(0,0,0,0.48)",lineHeight:1.7,marginBottom:"1rem",fontFamily:"'DM Sans',sans-serif" }}>{p.desc}</p>
                  <div style={{ display:"flex",flexWrap:"wrap",gap:5,marginBottom:"1.1rem" }}>
                    {p.tech.map((t,j) => <span key={j} className="chip" style={chip}>{t}</span>)}
                  </div>
                  <div style={{ display:"flex",gap:8,marginTop:"auto" }}>
                    <button className="pbtn" onClick={()=>open(p.live)} style={{ flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:5,padding:"9px",borderRadius:10,border:"none",fontWeight:600,fontSize:12.5,background:`linear-gradient(135deg,${p.accent},${p.accent}99)`,color:"#fff",boxShadow:`0 3px 12px ${p.accent}44`,fontFamily:"'DM Sans',sans-serif" }}>↗ Live Demo</button>
                    <button className="sbtn" onClick={()=>open(p.gh)} style={{ flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:5,padding:"9px",borderRadius:10,fontWeight:600,fontSize:12.5,background:"rgba(255,255,255,0.7)",border:"1px solid rgba(201,168,76,0.2)",color:"#5a3e0a",fontFamily:"'DM Sans',sans-serif" }}>⌥ Code</button>
                  </div>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          SKILLS
      ══════════════════════════════════ */}
      <section id="skills" style={{ position:"relative",zIndex:1,padding:isMobile?"3rem 1.2rem":"5rem 2rem" }}>
        <div style={{ maxWidth:1180,margin:"0 auto" }}>
          <SectionTitle sub="Tools & technologies I work with">Technical Arsenal</SectionTitle>
          <div style={{ display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fit,minmax(270px,1fr))",gap:"1rem" }}>
            {Object.entries(skills).map(([cat,list],i) => (
              <TiltCard key={i} intensity={7} disabled={isMobile} style={{ borderRadius:16 }}>
                <div style={{ ...frost,padding:"1.2rem" }}>
                  <h3 style={{ fontFamily:"'Playfair Display',serif",fontWeight:600,fontSize:11.5,color:"#b8860b",margin:"0 0 0.8rem",letterSpacing:"0.1em",textTransform:"uppercase",display:"flex",alignItems:"center",gap:5 }}>✦ {cat}</h3>
                  <div style={{ display:"flex",flexWrap:"wrap",gap:5 }}>
                    {list.map((s,j) => <span key={j} className="chip" style={chip}>{s}</span>)}
                  </div>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          EDUCATION & CERTS
      ══════════════════════════════════ */}
      <section id="education" style={{ position:"relative",zIndex:1,padding:isMobile?"3rem 1.2rem 4rem":"5rem 2rem 6rem" }}>
        <div style={{ maxWidth:1180,margin:"0 auto" }}>
          <SectionTitle>Education &amp; Certifications</SectionTitle>
          <div style={{ display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fit,minmax(320px,1fr))",gap:"2rem" }}>
            <div>
              <div style={{ display:"flex",alignItems:"center",gap:9,marginBottom:"1.2rem" }}>
                <span style={{ fontSize:18 }}>📚</span>
                <h2 style={{ fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:isMobile?17:20,color:"#1a1008",margin:0 }}>Education</h2>
              </div>
              <div style={{ display:"flex",flexDirection:"column",gap:"0.8rem" }}>
                {[
                  { deg:"Master of Computer Applications",  school:"Thakur Institute of Management Studies, Mumbai", years:"2024 – 2026", cgpa:"8.08/10", col:"#8b6914" },
                  { deg:"Bachelor of Science in IT",        school:"Ghanshyamdas Saraf College, Mumbai",             years:"2021 – 2024", cgpa:null,         col:"#6b7c93" },
                ].map((e,i) => (
                  <TiltCard key={i} intensity={5} disabled={isMobile} style={{ borderRadius:14 }}>
                    <div style={{ ...frost,padding:"1.1rem" }}>
                      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4,gap:8 }}>
                        <h3 style={{ fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:isMobile?13:14,color:"#1a1008",margin:0,flex:1 }}>{e.deg}</h3>
                        {e.cgpa && <span style={{ fontSize:12,color:"#2d7a4e",fontWeight:700,flexShrink:0 }}>{e.cgpa}</span>}
                      </div>
                      <p style={{ fontSize:12.5,color:e.col,margin:"0 0 3px",fontFamily:"'DM Sans',sans-serif",fontWeight:500 }}>{e.school}</p>
                      <p style={{ fontSize:11.5,color:"rgba(0,0,0,0.35)",margin:0,fontFamily:"'DM Sans',sans-serif" }}>{e.years}</p>
                    </div>
                  </TiltCard>
                ))}
              </div>
            </div>
            <div>
              <div style={{ display:"flex",alignItems:"center",gap:9,marginBottom:"1.2rem" }}>
                <span style={{ fontSize:18 }}>🏅</span>
                <h2 style={{ fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:isMobile?17:20,color:"#1a1008",margin:0 }}>Certifications</h2>
              </div>
              <div style={{ display:"flex",flexDirection:"column",gap:"0.55rem" }}>
                {certs.map((c,i) => (
                  <div key={i} className="cert" style={{ ...frost,padding:"0.8rem 1rem",display:"flex",alignItems:"center",gap:9,border:"1px solid rgba(201,168,76,0.12)",background:"rgba(255,255,255,0.5)" }}>
                    <div style={{ width:5,height:5,borderRadius:"50%",flexShrink:0,background:"rgba(184,134,11,0.25)" }} />
                    <div style={{ flex:1,minWidth:0 }}>
                      <p style={{ fontSize:isMobile?12:12.5,fontWeight:500,color:"#3d3020",margin:0,fontFamily:"'DM Sans',sans-serif" }}>{c.name}</p>
                      <p style={{ fontSize:10.5,color:"rgba(0,0,0,0.35)",margin:"2px 0 0",fontFamily:"'DM Sans',sans-serif" }}>{c.org}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ position:"relative",zIndex:1,padding:"2rem 1.2rem",borderTop:"1px solid rgba(201,168,76,0.15)",textAlign:"center",background:"rgba(255,255,255,0.4)",backdropFilter:"blur(12px)" }}>
        <div style={{ width:36,height:1,background:"linear-gradient(90deg,transparent,#c9a84c,transparent)",margin:"0 auto 1rem" }} />
        <p style={{ color:"rgba(0,0,0,0.3)",fontSize:12,marginBottom:5,fontFamily:"'Playfair Display',serif",fontStyle:"italic" }}>© 2026 Janhavi Chaturvedi · Crafted with passion and code</p>
        <p style={{ color:"rgba(0,0,0,0.2)",fontSize:11,marginBottom:"1rem",fontFamily:"'DM Sans',sans-serif",padding:"0 1rem" }}>
          Actively seeking Full-Time Roles &amp; Internships · Full-Stack / Software Dev / AI-ML / Fintech · Mumbai-based, open to Remote
        </p>
        <div style={{ display:"flex",justifyContent:"center",gap:6,flexWrap:"wrap" }}>
          {["Full-Stack Dev","ML Engineering","AI Integration","Fintech","Cloud Deployment"].map((t,i) => (
            <span key={i} style={{ ...chip,fontSize:10.5 }}>{t}</span>
          ))}
        </div>
      </footer>
    </div>
  );
}